import "draft-js/dist/Draft.css";
import React, { useRef, useEffect } from "react";
import Editor, { composeDecorators } from "draft-js-plugins-editor";
import createFocusPlugin from "draft-js-focus-plugin";
import createResizeablePlugin from "draft-js-resizeable-plugin";
import createImagePlugin from "draft-js-image-plugin";
import PropTypes from "prop-types";
import cn from "classnames";

import { Flex } from "../..";
import injectSheet from "../../hocs/injectSheet";
import { decoratorArray, styleMap, Table } from "../../utils/editorTexto/editorTexto";
import EditorTextoMedia from "../EditorTextoMedia";

const styles = theme => ({
  rootEditor: {
    overflow: "auto",
    "& .DraftEditor-root": {
      flex: "auto",
      cursor: ({ readOnly }) => (readOnly ? "not-allowed" : "text"),
      padding: 4,
      "& .draftJsFocusPlugin__focused__3Mksn": {
        cursor: "default",
        borderRadius: 2,
        boxShadow: "0 0 0 3px #0096fd",
      },
      "& .draftJsFocusPlugin__unfocused__1Wvrs:hover": {
        cursor: "default",
        borderRadius: 2,
        boxShadow: "0 0 0 3px #82ccff",
      },
      "& .DraftEditor-editorContainer": {
        height: "100%",
      },
    },
  },
  icone: {
    width: "20px !important",
    height: "20px !important",
    cursor: "pointer",
    fill: `${theme.palette.icons.darkInactiveIcon} !important`,
    "&:hover": {
      fill: `${theme.palette.icons.darkActiveIcon} !important`,
    },
  },
  botaoEnviar: {
    marginRight: 8,
  },
  hidePlaceholder: {
    "& .public-DraftEditorPlaceholder-root": {
      display: "none",
    },
  },
  "@global .DraftEditor-editorContainer figure": {
    display: "inline-block",
  },
});
const focusPlugin = createFocusPlugin();
const resizeablePlugin = createResizeablePlugin({
  horizontal: "absolute",
  vertical: "absolute",
});
const decorator = composeDecorators(focusPlugin.decorator, resizeablePlugin.decorator);
const imagePlugin = createImagePlugin({ decorator, imageComponent: EditorTextoMedia });

const plugins = [focusPlugin, resizeablePlugin, imagePlugin];

const EditorTexto = props => {
  const editorInstance = useRef(null);
  const {
    classes,
    handleAtalhosTeclado,
    handleOnBlur,
    sheet,
    theme,
    style,
    className,
    editorState,
    onChange,
    setFocus,
    ...rest
  } = props;

  useEffect(() => {
    if (setFocus) {
      setTimeout(focus, 200);
    }
  }, []);

  const focus = () => editorInstance.focus();
  const blockRendererFn = block => {
    const editorDomNode = editorInstance.current?.getEditorRef?.()?.editor;
    const type = block.getType();

    if (type === "table") {
      return {
        component: Table,
        editable: true,
        props: {
          editor: editorDomNode,
        },
      };
    }

    if (type === "atomic") {
      return {
        component: EditorTextoMedia,
        editable: false,
      };
    }
    return null;
  };

  const verificaNecessidadeEsconderPlaceholder = () => {
    const contentState = editorState.getCurrentContent();
    const hasStyle =
      contentState
        .getBlockMap()
        .first()
        .getType() !== "unstyled";

    return !contentState.hasText() && hasStyle;
  };
  const hidePlaceholder = verificaNecessidadeEsconderPlaceholder();

  return (
    <Flex
      style={style}
      id="campoMensagem"
      className={cn(classes.rootEditor, { [classes.hidePlaceholder]: hidePlaceholder }, className)}
      onClick={focus}
    >
      <Editor
        {...rest}
        blockRendererFn={blockRendererFn}
        handleKeyCommand={handleAtalhosTeclado}
        plugins={plugins}
        decorators={decoratorArray}
        onBlur={event => handleOnBlur(event, focus)}
        onChange={onChange}
        editorState={editorState}
        stripPastedStyles
        ref={editorInstance}
        customStyleMap={styleMap}
      />
    </Flex>
  );
};
EditorTexto.propTypes = {
  classes: PropTypes.object.isRequired,
  handleAtalhosTeclado: PropTypes.func,
  handleOnBlur: PropTypes.func,
  sheet: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
  editorState: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  setFocus: PropTypes.bool,
  keyBindingFn: PropTypes.func,
};
export { EditorTexto as EditorTextoPure };
export default injectSheet(styles)(EditorTexto);
